import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  Grid2 as Grid,
  Typography,
  Container,
  Snackbar,
  Alert,
  Paper,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { login, checkBranchOtp, registerBranch } from "../utils/api";
import { setDeviceId, getDeviceId, removeDeviceId } from "../utils/deviceAuth";
import { isAuthenticated } from "../utils/auth";
import Background from "../components/Background";

const LoginPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/");
    }
  }, []);

  const {
    register: registerLogin,
    handleSubmit: handleSubmitLogin,
    watch: watchLogin,
    formState: { errors: loginErrors },
  } = useForm();

  const {
    register: registerOTP,
    handleSubmit: handleSubmitOTP,
    watch: watchOTP,
    formState: { errors: otpErrors },
  } = useForm();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isOTPButtonDisabled, setIsOTPButtonDisabled] = useState(false);
  const [isDeviceAuthorized, setIsDeviceAuthorized] = useState(false);
  const [isDeviceRegistrationOpen, setIsDeviceRegistrationOpen] =
    useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsDeviceRegistrationOpen(false);

      const deviceId = getDeviceId();

      if (deviceId) {
        setIsDeviceAuthorized(true);
      } else {
        setIsDeviceAuthorized(false);

        const result = await checkBranchOtp();
        if (result?.data?.otp === true) {
          setIsDeviceRegistrationOpen(true);
        }
      }
    };

    fetchData();
  }, []);

  const handleRegister = () => {
    setIsDeviceAuthorized(false);
  };

  const onSubmit = async (data) => {
    try {
      setIsButtonDisabled(true);

      const username = data.username;
      const password = data.password;
      const deviceId = getDeviceId();

      const response = await login({ username, password, deviceId });
      if (response.status === 200) {
        const { token } = response.data;
        localStorage.setItem("token", token);
        navigate("/");
      } else {
        setSnackbarMessage(response.data.message || "Login failed");
        setOpenSnackbar(true);
        setIsButtonDisabled(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage(
        error.response.data.message ||
          "An error occurred. Please try again later."
      );
      setOpenSnackbar(true);
      setIsButtonDisabled(false);

      if (error.response.status === 403) {
        removeDeviceId();
        navigate("/");
      }
    }
  };

  const onSubmitOTP = async (data) => {
    try {
      setIsOTPButtonDisabled(true);
      const otp = data.otp;

      const response = await registerBranch({ otp });

      if (response.status === 200) {
        const deviceId = response?.data?.deviceId;
        if (deviceId) {
          setDeviceId(deviceId);
        }

        navigate("/");
      } else {
        setSnackbarMessage(response.data.message || "Failed");
        setOpenSnackbar(true);
        setIsOTPButtonDisabled(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage(
        error.response.data.message ||
          "An error occurred. Please try again later."
      );
      setOpenSnackbar(true);
      setIsOTPButtonDisabled(false);
    }
  };

  return (
    <>
      {!isDeviceAuthorized ? (
        isDeviceRegistrationOpen ? (
          <Container component="main" maxWidth="xs">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "90vh",
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  padding: "2rem",
                  paddingTop: "3rem",
                  paddingBottom: "4rem",
                }}
              >
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ marginBottom: 3 }}
                  gutterBottom
                >
                  Please enter the OTP sent by admin
                </Typography>
                <form onSubmit={handleSubmitOTP(onSubmitOTP)}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        {...registerOTP("otp", {
                          required: "OTP is required",
                        })}
                        error={!!otpErrors.otp}
                        helperText={otpErrors.otp?.message}
                        autoComplete="off"
                        value={(watchOTP("otp") || "").slice(0, 6)}
                        sx={{ input: { textAlign: "center" } }}
                        placeholder="XXXXXX"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={isOTPButtonDisabled}
                      >
                        Verify
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Box>
            <Snackbar
              open={openSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              autoHideDuration={5000}
              onClose={() => setOpenSnackbar(false)}
            >
              <Alert severity={"error"}>{snackbarMessage}</Alert>
            </Snackbar>
          </Container>
        ) : (
          <Container sx={{ margin: 3 }} align="center">
            <Typography variant="h2" marginTop={10}>
              403
            </Typography>
            <Typography variant="h6">Forbidden</Typography>
            <Typography variant="body1">
              Please contact the administrator
            </Typography>
          </Container>
        )
      ) : (
        <div>
          <Background />
          <Container component="main" maxWidth="xs">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "90vh",
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  padding: "2rem",
                  paddingTop: "3rem",
                  paddingBottom: "4rem",
                }}
              >
                <Typography
                  variant="h5"
                  align="center"
                  sx={{ fontWeight: "bold" }}
                  gutterBottom
                >
                  {process.env.REACT_APP_TITLE}
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ marginBottom: 3 }}
                  gutterBottom
                >
                  Please login to continue
                </Typography>
                <form onSubmit={handleSubmitLogin(onSubmit)}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Username"
                        variant="outlined"
                        {...registerLogin("username", {
                          required: "Username is required",
                        })}
                        error={!!loginErrors.username}
                        helperText={loginErrors.username?.message}
                        autoComplete="off"
                        value={(watchLogin("username") || "").slice(0, 50)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        variant="outlined"
                        {...registerLogin("password", {
                          required: "Password is required",
                        })}
                        value={(watchLogin("password") || "").slice(0, 200)}
                        error={!!loginErrors.password}
                        helperText={loginErrors.password?.message}
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={isButtonDisabled}
                      >
                        Login
                      </Button>
                    </Grid>
                    {isDeviceRegistrationOpen && (
                      <Grid size={{ xs: 12 }}>
                        <Button
                          variant="text"
                          size="small"
                          fullWidth
                          onClick={handleRegister}
                        >
                          Register this device
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </form>
              </Paper>
            </Box>
            <Snackbar
              open={openSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              autoHideDuration={5000}
              onClose={() => setOpenSnackbar(false)}
            >
              <Alert severity={"error"}>{snackbarMessage}</Alert>
            </Snackbar>
          </Container>
        </div>
      )}
    </>
  );
};

export default LoginPage;
